import React, { useEffect, useState } from 'react';
import {
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Collapse, IconButton, Typography, Box, Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ReportLayout, exportToCsv, useReportDates } from './ReportLayout';
import { reportsApi } from '../../api/reports';

function ChallanRow({ row }: { row: any }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.challan_number}</TableCell>
        <TableCell>{new Date(row.dispatch_date).toLocaleDateString('en-IN')}</TableCell>
        <TableCell align="right">{row.total_pieces}</TableCell>
        <TableCell align="right">{row.designs.length}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Design Breakdown</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Design No</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Size Breakdown</TableCell>
                    <TableCell>Part Breakdown</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.designs.map((d: any) => (
                    <TableRow key={d.design_no}>
                      <TableCell>{d.design_no}</TableCell>
                      <TableCell align="right">{d.total}</TableCell>
                      <TableCell>
                        {Object.entries(d.size_breakdown).map(([size, qty]) => (
                          <Chip key={size} label={`${size}: ${qty}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        {Object.entries(d.part_breakdown).map(([part, qty]) => (
                          <Chip key={part} label={`${part}: ${qty}`} size="small" sx={{ mr: 0.5 }} variant="outlined" />
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function DispatchDeliveryReport() {
  const { startDate, endDate, setStartDate, setEndDate } = useReportDates();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsApi.getDispatchDelivery({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const flatRows = data.flatMap((c) =>
    c.designs.map((d: any) => ({
      challan_number: c.challan_number,
      dispatch_date: new Date(c.dispatch_date).toLocaleDateString('en-IN'),
      design_no: d.design_no,
      total: d.total,
    }))
  );

  return (
    <ReportLayout
      title="Dispatch & Delivery Report"
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onExport={() => exportToCsv('dispatch-delivery.csv', flatRows)}
      loading={loading}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Challan No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Total Pieces</TableCell>
                <TableCell align="right">Designs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <ChallanRow key={row.challan_id} row={row} />
              ))}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">No dispatch records found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ReportLayout>
  );
}
